/**
 * A Birthday-Request funnel action carried across the register/login round-trip. The old app
 * resumed via an in-place authwall modal; the new app's auth is a page (OAuth full-page
 * redirect), so we stash the intent in sessionStorage — which survives the same-tab redirect —
 * exactly like `lib/pending-reminder.ts`. Set before sending a logged-out user to `/auth`;
 * consumed by `use-post-auth-funnel` once they come back authenticated.
 */
export type PendingBrAction =
  | {
      kind: "save-requester";
      token: string;
      label: string;
      /** Requester's first name — the congrats page after the save. */
      name?: string;
    }
  | {
      kind: "cake";
      /** A cake key (funnel-data `CakeKey`). */
      target?: string;
      /** Pre-key shapes (a stash from before the deploy): a note's author… */
      cardSendMessageId?: string;
      /** …or the card's sender. */
      cardSendId?: string;
      surface: "read" | "sign";
      label: string;
    };

const KEY = "bday_pending_br_action";

/** Older than this = the user plainly moved on; don't fire a stale action. */
const TTL_MS = 30 * 60_000;

export function setPendingBrAction(action: PendingBrAction): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...action, setAt: Date.now() }));
  } catch {
    /* private-mode / quota — non-fatal, the funnel just won't auto-resume */
  }
}

export function peekPendingBrAction(): PendingBrAction | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingBrAction & { setAt?: number };
    // Validate the shape — garbage under the key must not reach the resume hook.
    const valid =
      (parsed.kind === "save-requester" && typeof parsed.token === "string") ||
      (parsed.kind === "cake" &&
        (typeof parsed.target === "string" ||
          typeof parsed.cardSendMessageId === "string" ||
          typeof parsed.cardSendId === "string"));
    if (!valid || !parsed.setAt || Date.now() - parsed.setAt > TTL_MS) {
      clearPendingBrAction();
      return null;
    }
    return parsed;
  } catch {
    // Either the JSON was garbage or storage itself is off-limits (Safari with blocked site
    // data throws SecurityError on ANY sessionStorage access — BDAY-WEB-H). The cleanup must
    // not throw a second time out of this catch, so it goes through the guarded clear.
    clearPendingBrAction();
    return null;
  }
}

export function clearPendingBrAction(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Cake authwalls currently open on the page. While one is, the post-auth funnel leaves a stashed
 * cake action alone: the cake itself picks it up and shows legacy's confirm dialog (with the
 * person's state) instead of filing blind. Once it unmounts (e.g. register → /email-verify),
 * the funnel files it as before.
 */
export const liveCakeWalls = new Set<symbol>();
